import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faHourglass, faChevronDown, faChevronUp, faCheck, faXmark, faEye, faStar, faInfoCircle, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Popover } from "react-bootstrap";
import { runValidation, getValidationStatus, getValidationResults } from "../../../utils/validationApi";
import { getApiClient } from "../../../utils/apiAuth";
import AppPreviewModal from "./AppPreviewModal";

function ProjectVscEditor({ containerStatus = null, setValidationResults = () => {}, pollingRef = null, onEditorReadyRef = null, isReadOnly = false }) {
  const [vscodeUrl, setVscodeUrl] = useState(null);
  const [vscodeLoading, setVscodeLoading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [containerStatusState, setContainerStatusState] = useState('provisioning');
  
  // Validation state - always run all tasks from JSON files
  const [isRunning, setIsRunning] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);
  const [validationOutput, setValidationOutput] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewPath, setPreviewPath] = useState("");
  const [previewPort, setPreviewPort] = useState("5000");
  const [previewMinimized, setPreviewMinimized] = useState(false);

  // Per-project database credentials for the content creator (elevated privileges)
  const [dbCredentials, setDbCredentials] = useState(null);
  const [showDbHelp, setShowDbHelp] = useState(false);
  const [dbLoading, setDbLoading] = useState(false);
  const [dbError, setDbError] = useState(null);
  const dbFetchedRef = useRef(false);

  const outputRef = useRef(null);
  const isMountedRef = useRef(true);
  const iframeRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const readinessPollingRef = useRef(null);
  const readinessTimeoutRef = useRef(null);
  const iframeLoadTimeoutRef = useRef(null);
  const prevContainerStatusRef = useRef(null);
  const pollingStartedForRef = useRef(null);
  const startPollingRef = useRef(null);
  const hasSeenNullContainerRef = useRef(false);
  const editorLoadStartedRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    const iframe = iframeRef.current;
    return () => {
      isMountedRef.current = false;
      // Cleanup polling intervals
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (readinessPollingRef.current) {
        clearTimeout(readinessPollingRef.current);
        readinessPollingRef.current = null;
      }
      if (readinessTimeoutRef.current) {
        clearTimeout(readinessTimeoutRef.current);
        readinessTimeoutRef.current = null;
      }
      if (iframeLoadTimeoutRef.current) {
        clearTimeout(iframeLoadTimeoutRef.current);
        iframeLoadTimeoutRef.current = null;
      }
      if (iframe) {
        try {
          iframe.src = 'about:blank';
        } catch {
          // Ignore errors during cleanup
        }
      }
    };
  }, []);

  // Auto-scroll validation output
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [validationOutput]);

  const stopReadinessPolling = React.useCallback(() => {
    if (readinessPollingRef.current) {
      clearTimeout(readinessPollingRef.current);
      readinessPollingRef.current = null;
    }
    if (readinessTimeoutRef.current) {
      clearTimeout(readinessTimeoutRef.current);
      readinessTimeoutRef.current = null;
    }
  }, []);

  // Notify the sidebar that readiness polling has finished (ready, failed, or timed out)
  // so it can stop showing the "Creating..." button state.
  const notifyEditorReady = React.useCallback(() => {
    if (onEditorReadyRef && typeof onEditorReadyRef.current === "function") {
      onEditorReadyRef.current();
    }
  }, [onEditorReadyRef]);

  // Function to start polling container readiness - called when container is created or restored from sessionStorage
  const startContainerReadinessPolling = React.useCallback((containerStatusData) => {
    if (!containerStatusData?.success || !containerStatusData?.containerUrl) {
      return;
    }

    const containerUrl = containerStatusData.containerUrl;
    const containerName = containerStatusData.containerName || containerStatusData.containerId;
    const identifier = containerStatusData.student_id || containerStatusData.studentId || containerName;

    if (!identifier) {
      setVscodeUrl(null);
      setVscodeLoading(false);
      setIsPolling(false);
      return;
    }

    if (vscodeUrl === containerUrl && !vscodeLoading && !isPolling) {
      return;
    }

    stopReadinessPolling();
    editorLoadStartedRef.current = false;
    setVscodeLoading(true);
    setVscodeUrl(null);
    setIsPolling(true);

    const checkReadiness = async () => {
      try {
        const apiClient = getApiClient();
        const statusUrl = `api/student/vscode/status/${identifier}`;
        const response = await apiClient.get(statusUrl);

        const data = response.data;
        if (!data) return false;

        if (data.pod_status === "Pending") {
          if (isMountedRef.current) {
            setContainerStatusState('provisioning');
          }
        } else if (data.is_ready === true && data.pod_status === "Running") {
          if (isMountedRef.current) {
            setContainerStatusState('ready');
          }
        }

        const shouldLoad =
          data.is_ready === true &&
          data.pod_status === "Running";

        if (shouldLoad) {
          stopReadinessPolling();
          editorLoadStartedRef.current = true;
          notifyEditorReady();
          await new Promise(resolve => setTimeout(resolve, 5000));

          if (isMountedRef.current) {
            setVscodeUrl(containerUrl);
            setContainerStatusState('loading');
            setVscodeLoading(true);
            setIsPolling(false);

            if (iframeLoadTimeoutRef.current) {
              clearTimeout(iframeLoadTimeoutRef.current);
            }
            iframeLoadTimeoutRef.current = setTimeout(() => {
              if (isMountedRef.current) {
                setVscodeLoading(false);
                setIsPolling(false);
              }
              iframeLoadTimeoutRef.current = null;
            }, 15000);
          }
          return true;
        }

        if (data.pod_status === "Failed" || data.pod_status === "Error") {
          stopReadinessPolling();
          notifyEditorReady();
          setVscodeLoading(false);
          setIsPolling(false);
          setContainerStatusState('provisioning');
          return true;
        }

        return false;
      } catch (error) {
        return false;
      }
    };

    let pollCount = 0;
    const scheduleNextPoll = async () => {
      if (!isMountedRef.current) return;

      const shouldStop = await checkReadiness();
      if (shouldStop) {
        return;
      }

      pollCount++;
      const delay = pollCount <= 1 ? 3000 : pollCount <= 2 ? 5000 : 8000;
      readinessPollingRef.current = setTimeout(() => {
        scheduleNextPoll();
      }, delay);
    };

    (async () => {
      if (!isMountedRef.current) return;
      const shouldStop = await checkReadiness();
      if (!shouldStop && isMountedRef.current) {
        scheduleNextPoll();
      }
    })();

    readinessTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current && !editorLoadStartedRef.current) {
        console.warn("Container readiness timeout - loading VS Code anyway");
        stopReadinessPolling();
        editorLoadStartedRef.current = true;
        notifyEditorReady();
        setVscodeUrl(containerUrl);
        setContainerStatusState('loading');
        setVscodeLoading(true);
        setIsPolling(false);
      }
    }, 60000);
  }, [vscodeUrl, vscodeLoading, isPolling, stopReadinessPolling, notifyEditorReady]);

  // Keep ref updated so container-status effect can call without deps
  startPollingRef.current = startContainerReadinessPolling;

  // Show provisioning state when container creation is triggered; on reload (restored from sessionStorage), load iframe directly without pulse
  useEffect(() => {
    const prevStatus = prevContainerStatusRef.current;
    const currentStatus = containerStatus;
    
    if (!currentStatus) {
      hasSeenNullContainerRef.current = true;
      pollingStartedForRef.current = null;
      editorLoadStartedRef.current = false;
      stopReadinessPolling();
      setVscodeLoading(false);
      setIsPolling(false);
      setContainerStatusState('provisioning');
      setVscodeUrl(null);
    } else if (currentStatus?.success && currentStatus?.containerUrl) {
      const isReload = !hasSeenNullContainerRef.current; // Mounted with containerStatus already set (from sessionStorage)
      if (isReload) {
        // Page reload: load VS Code iframe directly without pulse (no API was called)
        setVscodeLoading(false);
        setIsPolling(false);
        setVscodeUrl(currentStatus.containerUrl);
      } else {
        // New container: show pulse and poll
        setVscodeLoading(true);
        setIsPolling(true);
        setContainerStatusState('ready');
        setVscodeUrl(null);
        const containerKey = currentStatus.containerUrl || currentStatus.containerId || currentStatus.containerName;
        if (pollingStartedForRef.current !== containerKey) {
          pollingStartedForRef.current = containerKey;
          startPollingRef.current?.(currentStatus);
        }
      }
    } else if (!prevStatus && currentStatus) {
      hasSeenNullContainerRef.current = true;
      setVscodeLoading(true);
      setIsPolling(true);
      setContainerStatusState('provisioning');
      setVscodeUrl(null);
    } else if (currentStatus && !currentStatus?.success) {
      setVscodeLoading(true);
      setIsPolling(true);
      setContainerStatusState('provisioning');
      setVscodeUrl(null);
    }
    
    prevContainerStatusRef.current = currentStatus;
  }, [containerStatus, stopReadinessPolling]);

  // Expose polling function via ref
  React.useEffect(() => {
    if (pollingRef) {
      pollingRef.current = startContainerReadinessPolling;
    }
  }, [pollingRef, startContainerReadinessPolling]);

  const handleIframeLoad = () => {
    stopReadinessPolling();
    editorLoadStartedRef.current = true;
    setVscodeLoading(false);
    setIsPolling(false);
    if (iframeLoadTimeoutRef.current) {
      clearTimeout(iframeLoadTimeoutRef.current);
      iframeLoadTimeoutRef.current = null;
    }
  };
  
  useEffect(() => {
    if (vscodeUrl && vscodeLoading && iframeRef.current) {
      let checkCount = 0;
      const maxChecks = 10;
      
      const checkIframeReady = setInterval(() => {
        checkCount++;
        const iframe = iframeRef.current;
        
        if (!iframe) {
          clearInterval(checkIframeReady);
          return;
        }
        
        // Check if iframe has loaded by checking if contentWindow exists
        if (iframe.contentWindow) {
          try {
            const href = iframe.contentWindow.location.href;
            if (href && href !== 'about:blank' && href !== window.location.href) {
              // Iframe has loaded and navigated to a different URL
              setVscodeLoading(false);
              setIsPolling(false);
              if (iframeLoadTimeoutRef.current) {
                clearTimeout(iframeLoadTimeoutRef.current);
                iframeLoadTimeoutRef.current = null;
              }
              clearInterval(checkIframeReady);
              return;
            }
          } catch (e) {
            setTimeout(() => {
              setVscodeLoading(false);
              setIsPolling(false);
              if (iframeLoadTimeoutRef.current) {
                clearTimeout(iframeLoadTimeoutRef.current);
                iframeLoadTimeoutRef.current = null;
              }
            }, 1000);
            clearInterval(checkIframeReady);
            return;
          }
        }
        
        if (checkCount >= maxChecks) {
          clearInterval(checkIframeReady);
        }
      }, 500); // Check every 500ms
      
      return () => clearInterval(checkIframeReady);
    }
  }, [vscodeUrl, vscodeLoading]);

  const handleIframeError = (e) => {
    console.error('Iframe load error:', e);
    setVscodeLoading(false);
    // Optionally show error message to user
  };

  const addValidationOutput = (message, type = 'info') => {
    setValidationOutput(prev => [...prev, { message, type, timestamp: new Date() }]);
  };

  const clearValidationOutput = () => {
    setValidationOutput([]);
    setTestCases([]);
    setSelectedTestCase(null);
    // Clear validation results when clearing output
    setValidationResults(null);
  };

  const handleCollapse = () => {
 
    setShowTerminal(!showTerminal);
  };

  // Replace placeholder tokens in error/message (fallback when backend sends raw placeholders)
  const replacePlaceholders = (text) => {
    if (!text || typeof text !== 'string') return text;
    return String(text)
      .replace(/\{requiredElements\}/gi, 'required element(s)')
      .replace(/\{requiredSelectors\}/gi, 'required selector(s)');
  };

  // Process validation results and extract test cases
  const processValidationResults = (results) => {
    // Store validation results for parent component
    setValidationResults(results);
    
    // Clear existing test cases at the start of processing new results
    const existingTestIds = new Set();
    const allTests = []; // Start fresh with empty array
    
    // Check if this is "all tasks" structure (has tasks array)
    if (results.tasks && Array.isArray(results.tasks)) {
      // Process each task's results
      results.tasks.forEach((taskResult, taskIndex) => {
        const taskPrefix = taskResult.taskName || taskResult.taskId || `Task ${taskIndex + 1}`;
        
        let taskHasTests = false;
        
        // Process tests array directly in taskResult (flattened structure from ValidationOrchestrator)
        // This is the aggregated array that contains all tests (executed and initialized)
        // Use a consistent testId format based on test name to avoid duplicates
        if (taskResult.tests && Array.isArray(taskResult.tests) && taskResult.tests.length > 0) {
          taskHasTests = true;
          taskResult.tests.forEach(test => {
            // Use test name as the primary identifier (consistent across all sections)
            const testName = test.name || test.id || '';
            const testId = `${taskPrefix}-${testName}`;
            
            // Only add if not already present (check by name to avoid duplicates)
            if (!existingTestIds.has(testId)) {
              const passed = test.passed !== undefined ? test.passed : (test.state === 'passed');
              const msg = replacePlaceholders(test.message || '');
              const err = replacePlaceholders(test.error || test.details || '');
              const errorDisplay = err || (!passed && msg ? msg : '');
              allTests.push({
                name: testName,
                description: test.description || '',
                passed,
                message: msg,
                error: errorDisplay,
                error_messages: test.error_messages,
                isMandatory: test.isMandatory === true
              });
              existingTestIds.add(testId);
            } else {
              // Update existing test if results changed (e.g., test status updated)
              const existingIndex = allTests.findIndex(t => {
                const tName = t.name || '';
                return tName === testName;
              });
              if (existingIndex >= 0) {
                const passed = test.passed !== undefined ? test.passed : (test.state === 'passed');
                const err = replacePlaceholders(test.error || test.details || allTests[existingIndex].error || '');
                const msg = replacePlaceholders(test.message || allTests[existingIndex].message || '');
                allTests[existingIndex] = {
                  ...allTests[existingIndex],
                  passed,
                  message: msg,
                  error: err || (!passed && msg ? msg : ''),
                  description: test.description || allTests[existingIndex].description,
                  error_messages: test.error_messages != null ? test.error_messages : allTests[existingIndex].error_messages,
                  isMandatory: test.isMandatory === true
                };
              }
            }
          });
        }
        // Schema tests - only if not already in flattened array
        if (taskResult.schema && taskResult.schema.tests && Array.isArray(taskResult.schema.tests) && taskResult.schema.tests.length > 0) {
          taskResult.schema.tests.forEach(test => {
            const testName = test.name || '';
            const testId = `${taskPrefix}-${testName}`;
            if (!existingTestIds.has(testId)) {
              taskHasTests = true;
              const passed = test.passed !== undefined ? test.passed : (test.state === 'passed');
              const msg = replacePlaceholders(test.message || '');
              const err = replacePlaceholders(test.error || test.details || '');
              allTests.push({
                name: testName,
                description: test.description || '',
                passed,
                message: msg,
                error: err || (!passed && msg ? msg : ''),
                error_messages: test.error_messages,
                isMandatory: test.isMandatory === true
              });
              existingTestIds.add(testId);
            }
          });
        }
        
        // Static tests - only if not already in flattened array
        if (taskResult.static && taskResult.static.tests && Array.isArray(taskResult.static.tests) && taskResult.static.tests.length > 0) {
          taskResult.static.tests.forEach(test => {
            const testName = test.name || '';
            const testId = `${taskPrefix}-${testName}`;
            if (!existingTestIds.has(testId)) {
              taskHasTests = true;
              const passed = test.passed !== undefined ? test.passed : (test.state === 'passed');
              const msg = replacePlaceholders(test.message || '');
              const err = replacePlaceholders(test.error || '');
              allTests.push({
                name: testName,
                description: test.description || '',
                passed,
                message: msg,
                error: err || (!passed && msg ? msg : ''),
                error_messages: test.error_messages,
                isMandatory: test.isMandatory === true
              });
              existingTestIds.add(testId);
            }
          });
        }
        
        // Pytest tests
        if (taskResult.pytest && taskResult.pytest.tests && Array.isArray(taskResult.pytest.tests) && taskResult.pytest.tests.length > 0) {
          taskResult.pytest.tests.forEach(test => {
            const testName = test.name || test.id || '';
            const testId = `${taskPrefix}-${testName}`;
            if (!existingTestIds.has(testId)) {
              taskHasTests = true;
              const passed = test.passed !== undefined ? test.passed : (test.state === 'passed');
              const msg = replacePlaceholders(test.message || '');
              const err = replacePlaceholders(test.error || '');
              const errorDisplay = err || (!passed && msg ? msg : '');
              allTests.push({
                name: testName,
                description: test.description || '',
                passed,
                message: msg,
                error: errorDisplay,
                error_messages: test.error_messages,
                isMandatory: test.isMandatory === true
              });
              existingTestIds.add(testId);
            } else {
              // Update existing test
              const existingIndex = allTests.findIndex(t => (t.name || '') === testName);
              if (existingIndex >= 0) {
                const passed = test.passed !== undefined ? test.passed : (test.state === 'passed');
                const msg = replacePlaceholders(test.message || '');
                const err = replacePlaceholders(test.error || '');
                const errorDisplay = err || (!passed && msg ? msg : '');
                allTests[existingIndex] = {
                  ...allTests[existingIndex],
                  passed,
                  message: msg || allTests[existingIndex].message,
                  error: errorDisplay || allTests[existingIndex].error,
                  description: test.description || allTests[existingIndex].description,
                  error_messages: test.error_messages != null ? test.error_messages : allTests[existingIndex].error_messages,
                  isMandatory: test.isMandatory === true
                };
              }
            }
          });
        }
        
        // Dynamic/Playwright tests - only if not already in flattened array
        if (taskResult.dynamic && taskResult.dynamic.tests && Array.isArray(taskResult.dynamic.tests) && taskResult.dynamic.tests.length > 0) {
          taskResult.dynamic.tests.forEach(test => {
            const testName = test.name || '';
            const testId = `${taskPrefix}-${testName}`;
            if (!existingTestIds.has(testId)) {
              taskHasTests = true;
              const passed = test.passed !== undefined ? test.passed : (test.state === 'passed');
              const msg = replacePlaceholders(test.message || '');
              const err = replacePlaceholders(test.error || '');
              const errorDisplay = err || (!passed && msg ? msg : '');
              allTests.push({
                name: testName,
                description: test.description || '',
                passed,
                message: msg,
                error: errorDisplay,
                error_messages: test.error_messages,
                isMandatory: test.isMandatory === true
              });
              existingTestIds.add(testId);
            } else {
              // Update existing test
              const existingIndex = allTests.findIndex(t => (t.name || '') === testName);
              if (existingIndex >= 0) {
                const passed = test.passed !== undefined ? test.passed : (test.state === 'passed');
                const msg = replacePlaceholders(test.message || '');
                const err = replacePlaceholders(test.error || '');
                const errorDisplay = err || (!passed && msg ? msg : '');
                allTests[existingIndex] = {
                  ...allTests[existingIndex],
                  passed,
                  message: msg || allTests[existingIndex].message,
                  error: errorDisplay || allTests[existingIndex].error,
                  description: test.description || allTests[existingIndex].description,
                  error_messages: test.error_messages != null ? test.error_messages : allTests[existingIndex].error_messages,
                  isMandatory: test.isMandatory === true
                };
              }
            }
          });
        }
        
        // If task has no tests but has errors, create a test case entry for the error
        if (!taskHasTests && taskResult.errors && Array.isArray(taskResult.errors) && taskResult.errors.length > 0) {
          taskResult.errors.forEach((errorMsg, errorIndex) => {
            const errorId = `${taskPrefix}-error-${errorIndex}`;
            if (!existingTestIds.has(errorId)) {
              allTests.push({
                name: `Validation Error ${errorIndex + 1 > 1 ? `(${errorIndex + 1})` : ''}`,
                id: errorId,
                description: '',
                passed: false,
                type: 'error',
                message: '',
                error: errorMsg || 'Validation failed',
                isMandatory: false
              });
              existingTestIds.add(errorId);
            }
          });
        } else if (!taskHasTests && !taskResult.passed) {
          // Task failed but no specific errors - create a generic error entry
          const errorId = `${taskPrefix}-validation-failed`;
          if (!existingTestIds.has(errorId)) {
            allTests.push({
              name: `Validation Failed`,
              id: errorId,
              description: '',
              passed: false,
              type: 'error',
              message: '',
              error: 'Task validation failed. Check validation output for details.',
              isMandatory: false
            });
              existingTestIds.add(errorId);
          }
        }
      });
      
      // If tasks array is empty but there are errors in the root results, show them
      if (results.tasks.length === 0 && results.errors && Array.isArray(results.errors) && results.errors.length > 0) {
        results.errors.forEach((errorMsg, errorIndex) => {
          const errorId = `root-error-${errorIndex}`;
          if (!existingTestIds.has(errorId)) {
            allTests.push({
              name: `Validation Error ${errorIndex + 1}`,
              id: errorId,
              description: '',
              passed: false,
              type: 'error',
              message: '',
              error: errorMsg || 'Validation failed',
              isMandatory: false
            });
            existingTestIds.add(errorId);
          }
        });
      }
    } else {
      // Single task structure (original format)
      // Check for tests array directly in results (aggregated array from ValidationOrchestrator)
      if (results.tests && Array.isArray(results.tests) && results.tests.length > 0) {
        results.tests.forEach(test => {
          const testId = test.id || test.name || '';
          if (!existingTestIds.has(testId)) {
            const passed = test.passed !== undefined ? test.passed : (test.state === 'passed');
            const msg = replacePlaceholders(test.message || '');
            const err = replacePlaceholders(test.error || test.details || '');
            allTests.push({
              name: test.name || 'Test',
              description: test.description || '',
              passed,
              message: msg,
              error: err || (!passed && msg ? msg : ''),
              error_messages: test.error_messages,
              isMandatory: test.isMandatory === true
            });
            existingTestIds.add(testId);
          }
        });
      } else {
        // Only process section-specific arrays if tests array doesn't exist
        // (for backward compatibility with old result format)
        if (results.schema && results.schema.tests) {
          results.schema.tests.forEach(test => {
            const testId = test.id || test.name || '';
            if (!existingTestIds.has(testId)) {
              const passed = test.passed !== undefined ? test.passed : (test.state === 'passed');
              const msg = replacePlaceholders(test.message || '');
              const err = replacePlaceholders(test.error || test.details || '');
              allTests.push({
                name: test.name || 'Schema Test',
                description: test.description || '',
                passed,
                message: msg,
                error: err || (!passed && msg ? msg : ''),
                error_messages: test.error_messages,
                isMandatory: test.isMandatory === true
              });
              existingTestIds.add(testId);
            }
          });
        }
        if (results.static && results.static.tests) {
          results.static.tests.forEach(test => {
            const testId = test.id || test.name || '';
            if (!existingTestIds.has(testId)) {
              const passed = test.passed !== undefined ? test.passed : (test.state === 'passed');
              const msg = replacePlaceholders(test.message || '');
              const err = replacePlaceholders(test.error || '');
              allTests.push({
                name: test.name || 'Static Test',
                description: test.description || '',
                passed,
                message: msg,
                error: err || (!passed && msg ? msg : ''),
                error_messages: test.error_messages,
                isMandatory: test.isMandatory === true
              });
              existingTestIds.add(testId);
            }
          });
        }
        if (results.pytest && results.pytest.tests) {
          results.pytest.tests.forEach(test => {
            const testId = test.id || test.name || '';
            if (!existingTestIds.has(testId)) {
              const passed = test.passed !== undefined ? test.passed : (test.state === 'passed');
              const msg = replacePlaceholders(test.message || '');
              const err = replacePlaceholders(test.error || '');
              const errorDisplay = err || (!passed && msg ? msg : '');
              allTests.push({
                name: test.name || 'Pytest Test',
                description: test.description || '',
                passed,
                message: msg,
                error: errorDisplay,
                error_messages: test.error_messages,
                isMandatory: test.isMandatory === true
              });
              existingTestIds.add(testId);
            } else {
              // Update existing test
              const existingIndex = allTests.findIndex(t => (t.name || '') === (test.name || test.id || ''));
              if (existingIndex >= 0) {
                const passed = test.passed !== undefined ? test.passed : (test.state === 'passed');
                const msg = replacePlaceholders(test.message || '');
                const err = replacePlaceholders(test.error || '');
                const errorDisplay = err || (!passed && msg ? msg : '');
                allTests[existingIndex] = {
                  ...allTests[existingIndex],
                  passed,
                  message: msg || allTests[existingIndex].message,
                  error: errorDisplay || allTests[existingIndex].error,
                  description: test.description || allTests[existingIndex].description,
                  error_messages: test.error_messages != null ? test.error_messages : allTests[existingIndex].error_messages,
                  isMandatory: test.isMandatory === true
                };
              }
            }
          });
        }
        if (results.dynamic && results.dynamic.tests) {
          results.dynamic.tests.forEach(test => {
            const testId = test.id || test.name || '';
            if (!existingTestIds.has(testId)) {
              const passed = test.passed !== undefined ? test.passed : (test.state === 'passed');
              const msg = replacePlaceholders(test.message || '');
              const err = replacePlaceholders(test.error || '');
              const errorDisplay = err || (!passed && msg ? msg : '');
              allTests.push({
                name: test.name || 'Playwright Test',
                description: test.description || '',
                passed,
                message: msg,
                error: errorDisplay,
                error_messages: test.error_messages,
                isMandatory: test.isMandatory === true
              });
              existingTestIds.add(testId);
            } else {
              // Update existing test
              const existingIndex = allTests.findIndex(t => (t.name || '') === (test.name || test.id || ''));
              if (existingIndex >= 0) {
                const passed = test.passed !== undefined ? test.passed : (test.state === 'passed');
                const msg = replacePlaceholders(test.message || '');
                const err = replacePlaceholders(test.error || '');
                const errorDisplay = err || (!passed && msg ? msg : '');
                allTests[existingIndex] = {
                  ...allTests[existingIndex],
                  passed,
                  message: msg || allTests[existingIndex].message,
                  error: errorDisplay || allTests[existingIndex].error,
                  description: test.description || allTests[existingIndex].description,
                  error_messages: test.error_messages != null ? test.error_messages : allTests[existingIndex].error_messages,
                  isMandatory: test.isMandatory === true
                };
              }
            }
          });
        }
      }
    }
    
    setTestCases(allTests);
    
    // Auto-select the first test case if available and none is selected
    if (allTests.length > 0) {
      setSelectedTestCase(allTests[0]);
    }
  };

  const startPollingValidation = (jobId) => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    let lastResultsHash = null; // Track if results changed to avoid unnecessary updates
    
    // Poll every 2 seconds
    pollingIntervalRef.current = setInterval(async () => {
      if (!isMountedRef.current || !jobId) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        return;
      }
      
      try {
        // Check status first
        const statusResponse = await getValidationStatus(jobId);
        
        if (statusResponse && statusResponse.success) {
          const status = statusResponse.status;
          
          // Always try to get results (even if still running) to get partial results
          try {
            const resultsResponse = await getValidationResults(jobId);
            if (resultsResponse && resultsResponse.success && resultsResponse.results) {
              // Create a hash of the results to detect changes
              const resultsHash = JSON.stringify(resultsResponse.results);
              
              // Only update if results changed (to avoid unnecessary re-renders)
              if (resultsHash !== lastResultsHash) {
                lastResultsHash = resultsHash;
                
                // Process results (this will update test cases incrementally)
                processValidationResults(resultsResponse.results);
              }
            }
          } catch (error) {
            // If results endpoint returns "still in progress", that's fine
            if (error.response?.status !== 400) {
              console.error('Error fetching validation results:', error);
            }
          }
          
          // If completed or failed, stop polling
          if (status === 'completed' || status === 'failed') {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            
            // Get final results one more time
            try {
              const resultsResponse = await getValidationResults(jobId);
              if (resultsResponse && resultsResponse.success && resultsResponse.results) {
                processValidationResults(resultsResponse.results);
              } else if (status === 'failed' && statusResponse.error) {
                addValidationOutput(`Validation failed: ${statusResponse.error}`, 'error');
              }
            } catch (error) {
              console.error('Error fetching final validation results:', error);
            }
            
            setIsRunning(false);
            setShowTerminal(true);
          }
        }
      } catch (error) {
        console.error('Error polling validation status:', error);
        // Don't stop polling on error, just log it
        // The job might still be running
      }
    }, 2000); // Poll every 2 seconds
  };

  // Resolve the content creator identity + project needed for the DB credentials APIs.
  const resolveDbContext = () => {
    let userId = sessionStorage.getItem("userId");
    if (!userId) {
      try {
        const token = sessionStorage.getItem("access_token");
        if (token) {
          const payload = JSON.parse(
            atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
          );
          userId = payload.user_id || null;
          if (userId) sessionStorage.setItem("userId", userId);
        }
      } catch {
        userId = null;
      }
    }

    const rawProjectId = sessionStorage.getItem("contentCreation_projectId");
    let projectId = null;
    if (rawProjectId != null) {
      try {
        projectId = parseInt(JSON.parse(rawProjectId), 10);
      } catch {
        projectId = parseInt(rawProjectId, 10);
      }
      if (Number.isNaN(projectId)) projectId = null;
    }

    return { userId, projectId };
  };

  // Create (get-or-create) or refresh the per-project DB credentials for the CC.
  // `create` is idempotent; `refresh` rotates the password + re-applies elevated privileges.
  const fetchDbCredentials = React.useCallback(async ({ refresh = false } = {}) => {
    const { userId, projectId } = resolveDbContext();
    if (!userId || !projectId) {
      setDbError("Missing content creator or project details. Reopen the project and try again.");
      return;
    }

    setDbLoading(true);
    setDbError(null);
    try {
      const apiClient = getApiClient();
      const endpoint = refresh
        ? "api/content-creation/project-db-credentials/refresh"
        : "api/content-creation/project-db-credentials/create";
      const response = await apiClient.post(endpoint, {
        user_id: userId,
        project_id: projectId,
      });

      if (isMountedRef.current) {
        setDbCredentials(response.data);
        dbFetchedRef.current = true;
      }
    } catch (error) {
      if (isMountedRef.current) {
        setDbError(
          error.response?.data?.detail ||
            error.message ||
            "Failed to load database credentials."
        );
      }
    } finally {
      if (isMountedRef.current) setDbLoading(false);
    }
  }, []);

  // Lazily load credentials the first time the info popover is opened.
  const handleDbHelpToggle = (nextShow) => {
    setShowDbHelp(nextShow);
    if (nextShow && !dbFetchedRef.current && !dbLoading) {
      fetchDbCredentials();
    }
  };

  const handleRunValidation = async () => {
    clearValidationOutput();
    setIsRunning(true);

    try {
      // Get container ID from containerStatus (can be numeric ID or pod_name)
      const containerId = containerStatus?.containerId || containerStatus?.containerName || null;
      
      // Always pass null to run all JSON files in testing_config
      const response = await runValidation(null, null, containerId);
      
      if (response && response.success && response.job_id) {
        startPollingValidation(response.job_id);
      } else {
        const errorMsg = response?.detail?.error || response?.error || 'Failed to start validation';
        throw new Error(errorMsg);
      }
    } catch (error) {
      const errorMsg = error.message || 'Unknown error occurred';
      addValidationOutput(`Error: ${errorMsg}`, 'error');
      setIsRunning(false);
    }
  };

  const handleTestCaseClick = (testCase) => {
    setSelectedTestCase(testCase);
  };

  const getOutputLineClass = (type) => {
    switch (type) {
      case 'error': return 'text-danger';
      case 'success': return 'text-success';
      case 'warning': return 'text-warning';
      default: return 'text-info';
    }
  };

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.2);
          }
        }
      `}</style>
      <div className="d-flex flex-column w-100 h-100" style={{ height: '100vh', minHeight: 0, overflow: 'hidden' }}>
      {/* VSCode Container */}
      <div className="position-relative" style={{ flex: '1 1 auto', minHeight: 0, height: showTerminal ? 'calc(50% - 60px)' : 'calc(100% - 60px)' }}>
        {!containerStatus && !vscodeLoading && !isPolling ? (
          // Placeholder when container is not created
          <div className="d-flex flex-column justify-content-center align-items-center h-100 bg-light">
            <div className="text-center p-5">
              <div className="mb-4">
                <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
                </svg>
              </div>
              <h4 className="text-muted mb-3">VS Code Editor</h4>
              <p className="text-muted mb-0">
                Please create a container from the sidebar to load the editor
              </p>
            </div>
          </div>
        ) : !vscodeUrl ? (
          // Show loading state while provisioning / waiting for access URL
          <div className="d-flex flex-column justify-content-center align-items-center h-100 bg-white" style={{ zIndex: 10 }}>
            <div className="d-flex align-items-center justify-content-center mb-3">
              {(isPolling || vscodeLoading) && (
                <div 
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: 
                      containerStatusState === 'provisioning' ? '#dc3545' :
                      containerStatusState === 'ready' ? '#ffc107' :
                      '#28a745',
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }}
                />
              )}
            </div>
            <div className="text-center">
              <h5 className="text-muted mb-2">Loading VS Code Editor...</h5>
              <p className="text-muted small mb-0">
                {containerStatusState === 'provisioning' ? 'Provisioning container...' :
                 containerStatusState === 'ready' ? 'Preparing to start code editor...' :
                 'Loading code editor...'}
              </p>
            </div>
          </div>
        ) : (
          <div className="position-relative w-100 h-100">
            {vscodeLoading && (
              <div
                className="d-flex flex-column justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100 bg-white"
                style={{ zIndex: 10 }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#28a745',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
                <div className="text-center mt-3">
                  <h5 className="text-muted mb-2">Loading VS Code Editor...</h5>
                  <p className="text-muted small mb-0">Loading code editor...</p>
                </div>
              </div>
            )}
            <iframe
            key={vscodeUrl} // Force reload when URL changes
            ref={iframeRef}
            src={vscodeUrl}
            frameBorder="0"
            className="w-100 h-100 border-0"
            style={{
              ...(isReadOnly ? { pointerEvents: 'none' } : {}),
              visibility: vscodeLoading ? 'hidden' : 'visible',
            }}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            title="VS Code Editor"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-top-navigation-by-user-activation"
            allow="clipboard-read; clipboard-write; fullscreen"
          ></iframe>
          </div>
        )}
      </div>

      {/* Run and Submit Buttons */}
      <div className="d-flex justify-content-between gap-2 p-2 border-top bg-light" style={{ flexShrink: 0 }}>
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            style={{ minWidth: '100px' }}
            onClick={handleCollapse}
            disabled={isReadOnly}
          >
            <FontAwesomeIcon icon={showTerminal ? faChevronDown : faChevronUp} style={{ marginRight: '4px' }} />
            {showTerminal ? 'Collapse' : 'Expand'}
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            style={{ minWidth: '90px' }}
            onClick={() => setShowPreviewModal(true)}
            disabled={!vscodeUrl || isReadOnly}
            title="Preview app running in container (e.g. Flask on port 5000)"
          >
            <FontAwesomeIcon icon={faEye} style={{ marginRight: '4px' }} />
            Preview
          </button><OverlayTrigger
            trigger="click"
            placement="top"
            show={showDbHelp}
            onToggle={handleDbHelpToggle}
            rootClose
            overlay={
              <Popover id="cc-db-creds-popover" style={{ maxWidth: '400px' }}>
                <Popover.Header className="d-flex align-items-center justify-content-between py-2">
                  <span className="small fw-semibold">Database credentials</span>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ms-2 text-muted"
                    onClick={() => setShowDbHelp(false)}
                    aria-label="Close"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </Popover.Header>
                <Popover.Body className="small">
                  <p className="mb-2">
                    Use these credentials in your project to connect to the per-project MySQL database.
                    It has full schema control (including <strong>DROP</strong> and <strong>TRUNCATE</strong>) scoped to this project only.
                  </p>
                  {dbLoading ? (
                    <div className="d-flex align-items-center gap-2 text-muted">
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span>Loading credentials...</span>
                    </div>
 ) : dbError ? (
  <div className="alert alert-danger py-2 mb-2">{dbError}</div>
) : dbCredentials ? (
  <div className="p-2 bg-light rounded">
    <div className="font-monospace" style={{ fontSize: '0.75rem' }}>
      <div>Host: <code>{dbCredentials.host}</code></div>
      <div>Port: <code>{dbCredentials.port}</code></div>
      <div>Database: <code>{dbCredentials.database}</code></div>
      <div>User: <code>{dbCredentials.username}</code></div>
      <div>
        Password:{' '}
        {dbCredentials.password ? (
          <code>{dbCredentials.password}</code>
        ) : (
          <span className="text-muted">hidden — click Refresh to generate a new one</span>
        )}
      </div>
    </div>
  </div>
) : (
  <div className="text-muted">No credentials loaded.</div>
)}
<div className="d-flex justify-content-end mt-2">
  <button
    type="button"
    className="btn btn-outline-primary btn-sm"
    onClick={() => fetchDbCredentials({ refresh: true })}
    disabled={dbLoading}
  >
    <FontAwesomeIcon icon={faSyncAlt} className="me-1" spin={dbLoading} />
    Refresh password
  </button>
</div>
</Popover.Body>
</Popover>
}
><span
              className="text-primary ms-1 align-middle"
              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
              role="button"
              tabIndex={0}
              aria-label="Database credentials"
              title="Database credentials"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click(); }}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </span></OverlayTrigger>
        </div>
        <div>
          <button
            id="run-validation-btn"
            className="btn btn-primary"
            style={{ minWidth: '100px' }}
            onClick={handleRunValidation}
            disabled={isRunning || isReadOnly}
          >
            <FontAwesomeIcon icon={isRunning ? faHourglass : faPlay} style={{ marginRight: '4px' }} />
            {isRunning ? 'Running...' : 'Run'}
          </button>
        </div>
      </div>

      {/* Terminal Panel - Below VSCode */}
      {showTerminal && (
        <section 
          className="border-top bg-white d-flex flex-column"
          style={{ 
            height: '50%',
            flexShrink: 0,
            overflow: 'hidden',
            minHeight: 0
          }}
        >
          {/* Show spinner overlay when validation is running */}
          {isRunning ? (
            <div className="d-flex flex-column justify-content-center align-items-center h-100">
              <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mb-0">Validating...</p>
            </div>
          ) : (
            <>
              {/* Test Cases Sidebar and Main Output Area */}
              <div className="d-flex" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {/* Sidebar: Test Cases List */}
            <div className="border-end bg-light" style={{ width: '250px', overflowY: 'auto' }}>
              <div className="p-2 border-bottom bg-white">
                <strong className="small">Test Cases</strong>
              </div>
              <div className="list-group list-group-flush">
                {testCases.map((testCase, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`list-group-item list-group-item-action text-start border-0 ${selectedTestCase === testCase ? 'active' : ''} ${testCase.passed ? 'list-group-item-success' : 'list-group-item-danger'}`}
                    onClick={() => handleTestCaseClick(testCase)}
                    style={{ width: '100%' }}
                  >
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div className="flex-grow-1 min-w-0">
                        <div className="small fw-medium">
                          {testCase.name || `Testcase ${index + 1}`}
                          {testCase.isMandatory ? <span className="text-danger fw-bold ms-1" style={{ fontSize: '0.5em', verticalAlign: 'top' }}><FontAwesomeIcon icon={faStar} /></span> : ''}
                        </div>
                      </div>
                      <span className={`badge flex-shrink-0 ${testCase.passed ? 'bg-success' : 'bg-danger'}`}>
                        {testCase.passed ? (
                          <FontAwesomeIcon icon={faCheck} />
                        ) : (
                          <FontAwesomeIcon icon={faXmark} />
                        )}
                      </span>
                    </div>
                  </button>
                ))}
                {testCases.length === 0 && (
                  <div className="p-2 text-muted small text-center">
                    <div>No test cases yet</div>
                    <div className="mt-2" style={{ fontSize: '0.75rem' }}>Run validation to look for testcases</div>
                  </div>
                )}
              </div>
            </div>

            {/* Main Area: Test Case Details */}
            <div className="flex-grow-1 d-flex flex-column" style={{ overflowY: 'auto' }}>
              <div className="d-flex justify-content-between align-items-center p-2 border-bottom bg-light">
                <strong className="small">Test Case Details</strong>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={clearValidationOutput}
                >
                  Clear
                </button>
              </div>
              <div
                ref={outputRef}
                className="flex-grow-1 p-3"
                style={{ fontFamily: 'monospace', fontSize: '0.875rem', overflowY: 'auto' }}
              >
                {selectedTestCase ? (
                  <div>
                    <div className="mb-3">
                      <h6 className="fw-bold">{selectedTestCase.name}</h6>
                      {selectedTestCase.id && (
                        <div className="mb-2">
                          <strong>ID:</strong> <code>{selectedTestCase.id}</code>
                        </div>
                      )}
                      <div className="mb-2">
                        <span className={`badge ${selectedTestCase.passed ? 'bg-success' : 'bg-danger'}`}>
                          {selectedTestCase.passed ? 'Passed' : 'Failed'}
                        </span>
                        {selectedTestCase.type && (
                          <span className="badge bg-secondary ms-2">{selectedTestCase.type}</span>
                        )}
                      </div>
                      <div className="mb-2">
                        <strong>Description:</strong>{' '}
                        {selectedTestCase.description ? selectedTestCase.description : <span className="text-muted">No description</span>}
                      </div>
                    </div>
                    {selectedTestCase.passed && selectedTestCase.message && (
                      <div className="alert alert-success">
                        <strong>Success:</strong> {selectedTestCase.message}
                      </div>
                    )}
                    {!selectedTestCase.passed && (selectedTestCase.error || selectedTestCase.message) && (
                      <div className="alert alert-danger">
                        <strong>Error:</strong>
                        {Array.isArray(selectedTestCase.error_messages) && selectedTestCase.error_messages.length > 0 ? (
                          <ul className="mb-0 mt-2 list-unstyled">
                            {selectedTestCase.error_messages.map((em, i) => (
                              <li key={i} style={{ whiteSpace: 'pre-wrap' }}>{replacePlaceholders(em)}</li>
                            ))}
                          </ul>
                        ) : (
                          <pre className="mb-0 mt-2" style={{ whiteSpace: 'pre-wrap' }}>{replacePlaceholders(selectedTestCase.error || selectedTestCase.message)}</pre>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    {validationOutput.map((output, index) => (
                      <div key={index} className={`mb-2 ${getOutputLineClass(output.type)}`}>
                        {output.message}
                      </div>
                    ))}
                    {validationOutput.length === 0 && (
                      <div className="text-muted">No output yet. Click Validate to start validation.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
            </>
          )}
        </section>
      )}

      <AppPreviewModal
        show={showPreviewModal}
        onHide={() => {
          setShowPreviewModal(false);
          setPreviewMinimized(false);
        }}
        containerStatus={containerStatus}
        previewPath={previewPath}
        setPreviewPath={setPreviewPath}
        previewPort={previewPort}
        setPreviewPort={setPreviewPort}
        previewMinimized={previewMinimized}
        setPreviewMinimized={setPreviewMinimized}
      />
    </div>
    </>
  );
}

export default ProjectVscEditor;
